/**
 * ProTimeline.tsx
 * 
 * Timeline panel for the Pro editor.
 * Wraps the existing TimelinePlayer's timeline area with version pills.
 */

import React, { useMemo } from 'react';
import type { Clip, Track, MontageVersion } from '../../types';

interface ProTimelineProps {
    clips: Clip[];
    tracks: Track[];
    allClips: Clip[];
    updateClips: (updater: (prev: Clip[]) => Clip[], saveToHistory?: boolean) => void;
    setTracks: (tracks: Track[] | ((prev: Track[]) => Track[])) => void;
    selectedClipId: string | null;
    onClipSelect: (clip: Clip | null) => void;
    timelinePlayerRef: React.RefObject<any>;
    montageVersions: MontageVersion[];
    currentVersionId: string | null;
    onSelectVersion: (versionId: string) => void;
    onNavigateInto?: (clip: Clip) => void;
}

export const ProTimeline: React.FC<ProTimelineProps> = ({
    clips,
    tracks,
    allClips,
    updateClips,
    setTracks,
    selectedClipId,
    onClipSelect,
    timelinePlayerRef,
    montageVersions,
    currentVersionId,
    onSelectVersion,
    onNavigateInto,
}) => {
    // Only show versions header if there are multiple montage versions
    const hasVersions = montageVersions.length > 1;

    // Calculate timeline duration
    const timelineDuration = useMemo(() => {
        if (clips.length === 0) return 30; // Default 30s
        return Math.max(...clips.map(c => c.start + c.duration), 10);
    }, [clips]);

    return (
        <div className="pro-timeline">
            {/* Version pills header */}
            {hasVersions && (
                <div className="pro-timeline__header">
                    <div className="pro-timeline__versions">
                        {montageVersions.map(version => (
                            <button
                                key={version.id}
                                className={`pro-timeline__version-pill ${currentVersionId === version.id ? 'active' : ''}`}
                                onClick={() => onSelectVersion(version.id)}
                                title={version.prompt || version.name}
                            >
                                {version.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Timeline content — reuse existing TimelineArea or render a simple placeholder */}
            <div className="pro-timeline__content">
                {clips.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'var(--pro-text-muted)',
                        fontSize: '13px'
                    }}>
                        Déposez des fichiers pour commencer le montage
                    </div>
                ) : (
                    <div style={{ height: '100%', overflow: 'hidden' }}>
                        {/* Reuse track labels + clip elements from existing TimelinePlayer */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            overflow: 'auto',
                            padding: '4px 0'
                        }}>
                            {tracks
                                .filter(track => track.isVisible)
                                .map(track => {
                                    const trackClips = clips.filter(c => c.trackId === track.id);
                                    return (
                                        <div key={track.id} style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            minHeight: '36px',
                                            borderBottom: '1px solid var(--pro-border-subtle)'
                                        }}>
                                            {/* Track label */}
                                            <div style={{
                                                width: '40px',
                                                flexShrink: 0,
                                                padding: '4px 8px',
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                color: 'var(--pro-text-muted)',
                                                textAlign: 'center',
                                                borderRight: '1px solid var(--pro-border-subtle)'
                                            }}>
                                                {track.label || track.id}
                                            </div>

                                            {/* Track clips */}
                                            <div style={{
                                                flex: 1,
                                                position: 'relative',
                                                height: '32px',
                                                overflow: 'hidden'
                                            }}>
                                                {trackClips.map(clip => {
                                                    const left = (clip.start / timelineDuration) * 100;
                                                    const width = (clip.duration / timelineDuration) * 100;
                                                    const isSelected = clip.id === selectedClipId;

                                                    return (
                                                        <div
                                                            key={clip.id}
                                                            onClick={() => onClipSelect(clip)}
                                                            onDoubleClick={() => {
                                                                if ((clip.type === 'synthe' || clip.type === 'panel') && onNavigateInto) {
                                                                    onNavigateInto(clip);
                                                                }
                                                            }}
                                                            style={{
                                                                position: 'absolute',
                                                                left: `${left}%`,
                                                                width: `${Math.max(width, 0.5)}%`,
                                                                top: '2px',
                                                                bottom: '2px',
                                                                borderRadius: '3px',
                                                                cursor: 'pointer',
                                                                overflow: 'hidden',
                                                                border: isSelected ? '1.5px solid var(--pro-accent)' : '1px solid var(--pro-border)',
                                                                background: isSelected
                                                                    ? 'var(--pro-accent-glow)'
                                                                    : clip.type === 'video' ? 'rgba(59, 130, 246, 0.25)'
                                                                        : clip.type === 'audio' ? 'rgba(34, 197, 94, 0.25)'
                                                                            : clip.type === 'image' ? 'rgba(168, 85, 247, 0.25)'
                                                                                : clip.type === 'text' ? 'rgba(251, 191, 36, 0.25)'
                                                                                    : clip.type === 'synthe' ? 'rgba(236, 72, 153, 0.25)'
                                                                                        : clip.type === 'panel' ? 'rgba(14, 165, 233, 0.25)'
                                                                                            : 'var(--pro-bg-elevated)',
                                                                transition: 'border-color 0.15s ease',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                paddingLeft: '4px',
                                                            }}
                                                            title={clip.name}
                                                        >
                                                            {/* Thumbnail preview */}
                                                            {clip.thumbnails && clip.thumbnails.length > 0 && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    inset: 0,
                                                                    display: 'flex',
                                                                    overflow: 'hidden',
                                                                    opacity: 0.3,
                                                                }}>
                                                                    {clip.thumbnails.slice(0, 5).map((thumb, i) => (
                                                                        <img
                                                                            key={i}
                                                                            src={thumb}
                                                                            alt=""
                                                                            style={{
                                                                                height: '100%',
                                                                                objectFit: 'cover',
                                                                                flex: 1,
                                                                                minWidth: 0
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Waveform for audio */}
                                                            {clip.waveform && clip.waveform.length > 0 && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    inset: 0,
                                                                    display: 'flex',
                                                                    alignItems: 'flex-end',
                                                                    gap: '1px',
                                                                    padding: '2px',
                                                                    opacity: 0.4,
                                                                }}>
                                                                    {clip.waveform.slice(0, 60).map((val, i) => (
                                                                        <div
                                                                            key={i}
                                                                            style={{
                                                                                flex: 1,
                                                                                background: 'rgba(34, 197, 94, 0.6)',
                                                                                minHeight: '1px',
                                                                                height: `${val * 100}%`,
                                                                                borderRadius: '1px'
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Clip name */}
                                                            <span style={{
                                                                position: 'relative',
                                                                zIndex: 1,
                                                                fontSize: '10px',
                                                                fontWeight: 500,
                                                                color: 'var(--pro-text-primary)',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                                                maxWidth: '100%',
                                                            }}>
                                                                {clip.name}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
